import React from 'react';
import { Loader2 } from 'lucide-react';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { DatePicker, Label } from '@/shared/components';
import { EstimateAcceptField } from './EstimateAcceptField';
import type { AdditionalVehicleDetailsFormInputs } from '../../schemas/additionalvehicledetails.schema';

interface ServiceHistoryDateFieldsProps {
    control: Control<AdditionalVehicleDetailsFormInputs>;
    watchedValues: Pick<AdditionalVehicleDetailsFormInputs, 'lastServiceDate' | 'lastServiceMileage'>;
    fieldIssues: Partial<Record<'lastMotDate' | 'lastServiceDate' | 'lastServiceMileage', string>>;
    isMotLoading: boolean;
    lastServiceMileageEstimate: number | null;
    onLastServiceDatePicked: (date: Date | undefined) => void;
}

export const LAST_SERVICE_MILEAGE_ESTIMATE_TOOLTIP =
    'Estimated from the current and annual mileage above, prorated back to the last service date. ' +
    'Select Use estimate while the field is empty to insert it, or enter the actual figure if you have it.';

export const ServiceHistoryDateFields: React.FC<ServiceHistoryDateFieldsProps> = ({
    control,
    watchedValues,
    fieldIssues,
    isMotLoading,
    lastServiceMileageEstimate,
    onLastServiceDatePicked,
}) => {
    return (
        <>
            <div>
                <Label htmlFor="lastMotDate">Last MOT Date</Label>
                <Controller
                    name="lastMotDate"
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                        // Authored copy for an unreadable seeded date is substituted into fieldIssues at construction.
                        // Not re-derived from field.value, which lags fieldIssues by a render or two after a flatpickr clear.
                        const message = error?.message ?? fieldIssues.lastMotDate;
                        const describedBy = message ? 'lastMotDate-message' : undefined;

                        return (
                            <>
                                <div className="relative">
                                    <DatePicker
                                        id="lastMotDate"
                                        aria-invalid={!!message}
                                        aria-describedby={describedBy}
                                        // DatePicker emits null on clear, which z.date().optional() rejects and would kill
                                        // auto-save. Not applied to registrationDate, where null and undefined are both invalid.
                                        onChange={(date) => field.onChange(date ?? undefined)}
                                        error={!!message}
                                        defaultDate={field.value}
                                        placeholder="Select last MOT date"
                                    />
                                    {isMotLoading && (
                                        <span
                                            className="absolute -translate-y-1/2 right-10 top-1/2 pointer-events-none text-mp-muted"
                                            aria-hidden
                                            data-testid="lastMotDate-loading"
                                        >
                                            <Loader2 className="size-4 animate-spin" />
                                        </span>
                                    )}
                                </div>
                                {message && (
                                    <p
                                        id="lastMotDate-message"
                                        role={error ? 'alert' : undefined}
                                        className="mt-1.5 text-xs text-mp-error"
                                    >
                                        {message}
                                    </p>
                                )}
                            </>
                        );
                    }}
                />
            </div>
            <div>
                <Label htmlFor="lastServiceDate">Last Service Date</Label>
                <Controller
                    name="lastServiceDate"
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                        const message = error?.message ?? fieldIssues.lastServiceDate;
                        const describedBy = message ? 'lastServiceDate-message' : undefined;

                        return (
                            <>
                                <DatePicker
                                    id="lastServiceDate"
                                    aria-invalid={!!message}
                                    aria-describedby={describedBy}
                                    onChange={(date) => {
                                        const picked = date ?? undefined;
                                        field.onChange(picked);
                                        onLastServiceDatePicked(picked);
                                    }}
                                    error={!!message}
                                    defaultDate={field.value}
                                    // Mirrors the schema's not-in-the-future rule so the picker cannot offer an invalid date.
                                    maxDate="today"
                                    placeholder="Select last service date"
                                />
                                {message && (
                                    <p
                                        id="lastServiceDate-message"
                                        role={error ? 'alert' : undefined}
                                        className="mt-1.5 text-xs text-mp-error"
                                    >
                                        {message}
                                    </p>
                                )}
                            </>
                        );
                    }}
                />
            </div>
            {watchedValues.lastServiceDate && (
                <Controller
                    name="lastServiceMileage"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <EstimateAcceptField
                            id="lastServiceMileage"
                            label="Last Service Mileage"
                            estimate={lastServiceMileageEstimate}
                            unitSuffix="mi"
                            emptyPlaceholder="Enter last service mileage"
                            tooltip={LAST_SERVICE_MILEAGE_ESTIMATE_TOOLTIP}
                            isRequired
                            inputMode="numeric"
                            // Sourced from watchedValues: useController cannot read back an explicitly-undefined value.
                            field={{ value: watchedValues.lastServiceMileage, onChange: field.onChange }}
                            error={error?.message ?? fieldIssues.lastServiceMileage}
                            errorId="lastServiceMileage-message"
                            hasFieldError={!!error}
                        />
                    )}
                />
            )}
        </>
    );
};
